import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

function PaginationButtons({
  currentPage,
  totalPages,
  onPageChange,
  className = "",
}) {
  if (totalPages <= 1) return null;

  // Calculate visible page range with smart ellipsis logic
  const calculateVisiblePageRange = (currentPage, totalPages) => {
    if (totalPages < 1) return [];
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set();
    pages.add(1);
    pages.add(2);
    pages.add(totalPages - 1);
    pages.add(totalPages);

    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p >= 1 && p <= totalPages) {
        pages.add(p);
      }
    }

    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sortedPages.length; i++) {
      if (i > 0 && sortedPages[i] !== sortedPages[i - 1] + 1) {
        result.push(-1); // -1 represents ellipsis
      }
      result.push(sortedPages[i]);
    }

    return result;
  };

  const visiblePageRange = calculateVisiblePageRange(currentPage, totalPages);
  const pageNumbers = [];

  // Previous button
  pageNumbers.push(
    <button
      key="prev"
      onClick={
        currentPage > 1 ? () => onPageChange(currentPage - 1) : undefined
      }
      disabled={currentPage === 1}
      className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
        currentPage === 1
          ? "bg-blue text-white cursor-not-allowed"
          : "bg-white border hover:bg-gray-50"
      }`}
      style={
        currentPage === 1 ? {} : { borderColor: "#878787", color: "#878787" }
      }
    >
      <CaretLeft className="w-4 h-4" weight="bold" />
    </button>
  );

  // Render page numbers and ellipses based on calculated range
  visiblePageRange.forEach((pageNum, index) => {
    if (pageNum === -1) {
      // Render ellipsis
      pageNumbers.push(
        <span
          key={`ellipsis-${index}`}
          className="px-2"
          style={{ color: "#878787" }}
        >
          ...
        </span>
      );
    } else {
      // Render page number
      pageNumbers.push(
        <button
          key={`page-${pageNum}`}
          onClick={() => onPageChange(pageNum)}
          className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
            pageNum === currentPage
              ? "bg-white border border-orange"
              : "bg-white border hover:bg-gray-50"
          }`}
          style={
            pageNum === currentPage
              ? { color: "#878787" }
              : { borderColor: "#878787", color: "#878787" }
          }
        >
          {pageNum}
        </button>
      );
    }
  });

  // Next button
  pageNumbers.push(
    <button
      key="next"
      onClick={
        currentPage < totalPages
          ? () => onPageChange(currentPage + 1)
          : undefined
      }
      disabled={currentPage === totalPages}
      className={`w-8 h-8 flex items-center justify-center rounded transition-colors ${
        currentPage === totalPages
          ? "bg-blue text-white cursor-not-allowed"
          : "bg-white border hover:bg-gray-50"
      }`}
      style={
        currentPage === totalPages
          ? {}
          : { borderColor: "#878787", color: "#878787" }
      }
    >
      <CaretRight className="w-4 h-4" weight="bold" />
    </button>
  );

  return (
    <div className={`flex items-center justify-center gap-2 mt-8 ${className}`}>
      {pageNumbers}
    </div>
  );
}

export default PaginationButtons;
